import { Component } from 'react'
import cn from 'classnames'
import { WrappedFieldProps, FieldValue, WrappedFieldInputProps } from 'redux-form'
import OldRadioButton from 'components/atoms/inputs/OldRadioButton'
import { State } from 'interfaces/state'
import { InputTarget } from 'interfaces/form'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import { remsToPixels } from 'helpers/styleHelpers'
import * as styles from 'styles/atoms/inputs/RadioButtonList.css'
import { Option } from 'interfaces/component'
import { QAMarked } from 'interfaces/component'

export type RadioButtonListProps<TValue = string> = WrappedFieldProps<State> & QAMarked & {
  error?: string
  label: string
  name: string
  items: Option<TValue>[]
  align: 'left' | 'right'

  onValueChange?: (value: FieldValue) => void
}

type RadioButtonListState = {
  shouldRenderWrap: boolean
}

class RadioButtonList extends Component<RadioButtonListProps, RadioButtonListState> {
  static defaultProps = {
    align: 'right',
  }

  state = {
    shouldRenderWrap: false,
  }

  private rootNode: HTMLElement | null = null
  private listNode: HTMLElement | null = null

  render () {
    const { label, name, items, onValueChange, qaMarker, error } = this.props
    const input: WrappedFieldInputProps | undefined = this.props.input

    if (!input) {
      return null
    }

    const { shouldRenderWrap } = this.state
    const className = cn({
      [styles.wrap]: shouldRenderWrap,
      [styles.inline]: !shouldRenderWrap,
      [styles.left]: this.props.align === 'left',
    })

    return (
      <div
        className={ className }
        ref={ (el) => this.rootNode = el }
        data-qa-marker={ qaMarker }
      >
        <div className={ styles.label }>{ label }</div>
        <ul
          className={ styles.buttonList }
          ref={ (el) => this.listNode = el }
        >
          { items.map((item, index) => (
            <li key={ index } className={ styles.buttonListItem }>
              <OldRadioButton
                name={ name }
                checked={ item.value === input.value }
                qaMarker={ item.value }
                onChange={ (e) => {
                  const target: InputTarget = e.target
                  if (onValueChange) {
                    onValueChange(target ? target.value : null)
                  }
                  input.onChange(e)
                } }
                { ...item }
              />
            </li>
          )) }
        </ul>
        <div className={ styles.error }>{ error }</div>
      </div>
    )
  }

  componentDidMount () {
    this.setShouldRenderWrap()
    // TODO @pmyagkov use `OuterEventTracker`
    window.addEventListener('resize', this.handleResizeDebounced)
  }

  componentDidUpdate (prevProps: RadioButtonListProps) {
    if (prevProps.label !== this.props.label || !isEqual(prevProps.items, this.props.items)) {
      this.setShouldRenderWrap()
    }
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResizeDebounced)
    this.handleResizeDebounced.cancel()
  }

  private setShouldRenderWrap = () => {
    if (!this.rootNode || !this.listNode) {
      return
    }

    const containerActualSize = this.rootNode.getBoundingClientRect()
    const containerActualHeight = containerActualSize.height
    const errorHeight = parseFloat(styles.errorFontSize) + parseFloat(styles.errorMarginTop)
    const cssHeight = remsToPixels(styles.inlineHeight) * (this.props.align === 'left' ? 2 : 1) + errorHeight
    // `1.5` is for safety here
    const isHeightGreater = containerActualHeight > cssHeight * 1.5

    const containerActualWidth = containerActualSize.width
    const listActualWidth = this.listNode.getBoundingClientRect().width
    const isWidthGreater = listActualWidth > containerActualWidth

    const shouldRenderWrap = isHeightGreater || isWidthGreater

    if (shouldRenderWrap !== this.state.shouldRenderWrap) {
      this.setState({ shouldRenderWrap })
    }
  }

  private handleResizeDebounced = debounce(this.setShouldRenderWrap, 50)
}

export default RadioButtonList
